import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../utils/scrollUtils';
import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderProps {
  language: 'fr' | 'en' | 'ar';
  setLanguage: (lang: 'fr' | 'en' | 'ar') => void;
  t: any;
  isRTL: boolean;
  showOnlyHome?: boolean;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  selectedCategory?: string;
  setSelectedCategory?: (category: string) => void;
  viewMode?: 'grid' | 'list';
  setViewMode?: (mode: 'grid' | 'list') => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
}

const HeaderContainer = styled(motion.header)<{ $isMinimized: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  box-shadow: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    transform: ${props => props.$isMinimized ? "translateY(-100%)" : "translateY(0)"};
  }
`;

const Nav = styled.nav<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.2rem;
  }
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 60px;
  height: 60px;
  
  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;

const Title = styled.div<{ $isRTL: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  
  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover img {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const NavLinks = styled.ul<{ $isRTL: boolean }>`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  padding: 0;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.li<{ $isActive?: boolean; $isRTL?: boolean }>`
  position: relative;
  
  a {
    color: ${props => props.$isActive ? '#dc2626' : '#374151'};
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.05)' : 'transparent'};
    transform: ${props => props.$isActive ? 'translateY(-1px)' : 'translateY(0)'};
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    
    &:hover {
      color: #dc2626;
      background: rgba(220, 38, 38, 0.05);
      transform: translateY(-1px);
    }
    
    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 50%;
      width: ${props => props.$isActive ? '80%' : '0'};
      height: 2px;
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      transform: translateX(-50%);
    }
    
    &:hover::after {
      width: 80%;
    }
  }
`;

const HomeButton = styled.button<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.95rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 38, 38, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LanguageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 1rem;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LanguageIcon = styled(motion.button)<{ $isRTL: boolean; $isActive: boolean }>`
  width: 36px;
  height: 36px;
  border: 2px solid ${props => props.$isActive ? '#dc2626' : 'rgba(220, 38, 38, 0.3)'};
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'rgba(255, 255, 255, 0.9)'};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-weight: 700;
  font-size: 0.8rem;
  color: #dc2626;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  position: relative;
  overflow: hidden;
  z-index: 1001;
  
  &:hover {
    background: rgba(220, 38, 38, 0.1);
    border-color: #dc2626;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    background: rgba(220, 38, 38, 0.1);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const SearchSection = styled.div<{ $isRTL: boolean; $isMinimized: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 1rem;
  margin-left: 1rem;
  margin-right: 1rem;
  padding-right: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(220, 38, 38, 0.1);
  
  @media (max-width: 768px) {
    max-height: ${props => props.$isMinimized ? '0' : '200px'};
    overflow: hidden;
    opacity: ${props => props.$isMinimized ? '0' : '1'};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @media (min-width: 769px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }
`;

const SearchInput = styled.input<{ $isRTL: boolean }>`
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  width: 100%;
  background: #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
  
  @media (min-width: 769px) {
    max-width: 600px;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
  
  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    gap: 0.4rem;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5rem 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const CategoryTab = styled(motion.button)<{ $isActive: boolean; $isRTL: boolean }>`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.$isActive ? '#dc2626' : '#e2e8f0'};
  background: ${props => props.$isActive ? '#dc2626' : 'white'};
  color: ${props => props.$isActive ? 'white' : '#64748b'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  white-space: nowrap;
  
  &:hover {
    border-color: #dc2626;
    background: ${props => props.$isActive ? '#dc2626' : '#fef2f2'};
    color: ${props => props.$isActive ? 'white' : '#dc2626'};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    align-self: center;
  }
`;

const ViewButton = styled.button<{ $isActive: boolean }>`
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.$isActive ? '#dc2626' : 'transparent'};
  color: ${props => props.$isActive ? 'white' : '#64748b'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
    flex-shrink: 0;
    white-space: nowrap;
  font-weight: 600;
  
  &:hover {
    background: ${props => props.$isActive ? '#dc2626' : '#e2e8f0'};
    color: ${props => props.$isActive ? 'white' : '#374151'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

const ClearFiltersButton = styled(motion.button)<{ $isRTL: boolean }>`
  padding: 0.5rem 1rem;
  border: 2px solid #6b7280;
  background: white;
  color: #6b7280;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  &:hover {
    border-color: #374151;
    background: #f9fafb;
    color: #374151;
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    flex-shrink: 0;
    white-space: nowrap;
    align-self: center;
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean; $isRTL?: boolean }>`
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  border: 1px solid rgba(220, 38, 38, 0.1);
  border-radius: 12px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const HamburgerLine = styled.span<{ $isOpen: boolean }>`
  width: 20px;
  height: 2px;
  background: ${props => props.$isOpen ? '#ffffff' : '#dc2626'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  
  &:nth-child(1) {
    transform: ${props => props.$isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'rotate(0)'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.$isOpen ? '0' : '1'};
    transform: ${props => props.$isOpen ? 'scale(0)' : 'scale(1)'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.$isOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'rotate(0)'};
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  z-index: 999;
`;

const MobileMenuContainer = styled(motion.div)<{ $isRTL: boolean }>`
  position: fixed;
  top: 0;
  ${props => props.$isRTL ? 'right: 0;' : 'left: 0;'}
  width: 320px;
  height: 100vh;
  background: white;
  box-shadow: ${props => props.$isRTL ? 
    '-8px 0 32px rgba(0, 0, 0, 0.2)' : 
    '8px 0 32px rgba(0, 0, 0, 0.2)'};
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MobileMenuContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1.5rem;
`;

const MobileMenuFooter = styled.div<{ $isRTL: boolean }>`
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MobileLanguageTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const MobileLanguageButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;

const MobileLanguageButton = styled(motion.button)<{ $isActive: boolean; $isRTL: boolean }>`
  width: 44px;
  height: 44px;
  border: 2px solid ${props => props.$isActive ? '#dc2626' : 'rgba(220, 38, 38, 0.3)'};
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'white'};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #dc2626;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    background: rgba(220, 38, 38, 0.1);
    border-color: #dc2626;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MobileMenuHeader = styled.div<{ $isRTL: boolean }>`
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const MobileMenuTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileNavLink = styled.div<{ $isActive?: boolean; $isRTL?: boolean }>`
  padding: 1rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.$isActive ? 'rgba(220, 38, 38, 0.1)' : 'transparent'};
  color: ${props => props.$isActive ? '#dc2626' : '#374151'};
  font-weight: ${props => props.$isActive ? '600' : '500'};
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
    transform: translateX(4px);
  }
`;

const Header: React.FC<HeaderProps> = ({ 
  language, 
  setLanguage, 
  t, 
  isRTL, 
  showOnlyHome = false,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  viewMode,
  setViewMode,
  onClearFilters,
  hasActiveFilters
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(false);
  const [isSearchMinimized, setIsSearchMinimized] = useState(false);
  const lastScrollY = useRef(0);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const navigate = useNavigate();
  const location = useLocation();
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const isProductsPage = location.pathname === '/products';

  useEffect(() => {
    // Route-based active section detection
    const pathname = location.pathname;
    
    if (pathname === '/solaire' || pathname === '/installation-service') {
      setActiveSection('services');
    } else if (pathname === '/products' || pathname.startsWith('/category/') || pathname.startsWith('/product/')) {
      setActiveSection('products');
    } else if (pathname === '/about') {
      setActiveSection('about');
    } else if (pathname === '/markets') {
      setActiveSection('markets');
    } else if (pathname === '/partners') {
      setActiveSection('partners');
    } else if (pathname === '/contact') {
      setActiveSection('contact');
    } else if (pathname === '/') {
      // Only use scroll detection on home page
      // The scroll detection logic will handle this
    }
  }, [location.pathname]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Mobile header minimization logic
      if (window.innerWidth <= 768) {
        if (currentScrollY > lastScrollY.current && currentScrollY > 10) {
          // Scrolling down - minimize entire header
          setIsHeaderMinimized(true);
          if (isProductsPage) {
            setIsSearchMinimized(true);
          }
        } else if (currentScrollY < lastScrollY.current) {
          // Scrolling up - expand header
          setIsHeaderMinimized(false);
          if (isProductsPage) {
            setIsSearchMinimized(false);
          }
        }
      }
      
      lastScrollY.current = currentScrollY;
      setIsScrolled(window.scrollY > 50);
      
      // Only use scroll detection on home page
      if (location.pathname === '/') {
      // Debounce scroll detection for better performance
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!showOnlyHome) {
                                                                      // Very simple scroll detection
          const scrollPosition = window.scrollY + 200;
          let currentSection = 'home';
          
          // Get all sections and check which one we're in
          const sections = [
            { id: 'discover-products', element: document.getElementById('discover-products') },
            { id: 'trusted-clients', element: document.getElementById('trusted-clients') },
            { id: 'stats', element: document.getElementById('stats') },
            { id: 'about', element: document.getElementById('about') },
            { id: 'services', element: document.getElementById('services') },
            { id: 'markets', element: document.getElementById('markets') },
            { id: 'partners', element: document.getElementById('partners') },
            { id: 'contact', element: document.getElementById('contact') }
          ];
          
          // Check each section
          for (const section of sections) {
            if (section.element) {
              const sectionTop = section.element.offsetTop;
              const sectionBottom = sectionTop + section.element.offsetHeight;
              
              if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                // Special handling for certain sections
                if (section.id === 'discover-products') {
                  currentSection = 'discover-products';
                } else if (section.id === 'stats' || section.id === 'trusted-clients') {
                  currentSection = 'partners';
                } else {
                  currentSection = section.id;
                }
                break;
              }
            }
          }
          
          setActiveSection(currentSection);
        }
      }, 10); // Small debounce for smooth performance
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call to set active section on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [showOnlyHome, isProductsPage, location.pathname]);

  const handleNavClick = (sectionId: string) => {
    if (showOnlyHome) {
      navigate('/');
    } else {
      scrollToSection(sectionId);
      // Immediately update active section when clicked
      setActiveSection(sectionId);
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (showOnlyHome) {
      navigate('/');
    } else {
      scrollToSection('home');
    }
  };

  const handleSearchTap = () => {
    if (window.innerWidth <= 768 && isSearchMinimized) {
      setIsSearchMinimized(false);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleLanguageChange = (newLanguage: 'fr' | 'en' | 'ar') => {
    setLanguage(newLanguage);
    localStorage.setItem('techyid-language', newLanguage);
    setIsLanguageMenuOpen(false);
  };

  const getLanguageButtons = () => {
    const languages: ('fr' | 'en' | 'ar')[] = ['fr', 'en', 'ar'];
    const currentIndex = languages.indexOf(language);
    
    if (!isLanguageMenuOpen) {
      // Show only current language button
      return [
        <LanguageIcon
          key={language}
          $isRTL={isRTL}
          $isActive={true}
          onClick={() => setIsLanguageMenuOpen(true)}
          aria-label={`Select language. Current: ${language.toUpperCase()}`}
        >
          {getLanguageDisplay()}
        </LanguageIcon>
      ];
    }
    
    // Show all language buttons
    return languages.map((lang) => (
      <LanguageIcon
        key={lang}
        $isRTL={isRTL}
        $isActive={lang === language}
        onClick={() => handleLanguageChange(lang)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ duration: 0.2, delay: languages.indexOf(lang) * 0.1 }}
        aria-label={`Switch to ${lang.toUpperCase()}`}
      >
        {lang === 'fr' ? 'FR' : lang === 'en' ? 'EN' : 'ع'}
      </LanguageIcon>
    ));
  };

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };

    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageMenuOpen]);

  const getLanguageDisplay = () => {
    switch (language) {
      case 'fr': return 'FR';
      case 'en': return 'EN';
      case 'ar': return 'ع';
      default: return 'FR';
    }
  };

  const navItems = showOnlyHome 
    ? [{ id: 'home', label: t.home }]
    : [
        { id: 'home', label: t.home },
        { id: 'about', label: t.about },
        { id: 'discover-products', label: t.products },
        { id: 'services', label: t.services },
        { id: 'markets', label: t.markets },
        { id: 'partners', label: t.partners },
        { id: 'contact', label: t.contact },
      ];

  const categories = [
    { id: '0', label: t.materialEquipment || 'Material & Equipment' },
    { id: '1', label: t.groupesElectrogenes || 'Groupes Electrogènes' },
    { id: '2', label: t.accessoriesElectriques || 'Accessories Electriques' },
  ];

  return (
    <>
      <HeaderContainer $isMinimized={isHeaderMinimized}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          background: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
          boxShadow: 'none'
        }}
      >
        <Nav $isRTL={isRTL}>
          <BrandContainer onClick={handleLogoClick}>
            <Logo>
              <img src="/images/logo192.png" alt={t.altTexts?.logo || 'Logo'} />
            </Logo>
            <Title $isRTL={isRTL}>
              <img src={language === 'ar' ? "/images/textar.png" : "/images/text.png"} alt="TECHYID" />
            </Title>
          </BrandContainer>
          
          {showOnlyHome ? (
            <HomeButton $isRTL={isRTL} onClick={handleHomeClick}>
              {t.home || 'Accueil'}
            </HomeButton>
          ) : (
            <>
              <NavLinks $isRTL={isRTL}>
                {navItems.map((item) => (
                  <NavLink key={item.id} $isActive={activeSection === item.id} $isRTL={isRTL}>
                    <a href={`#${item.id}`} onClick={(e) => { e.preventDefault(); handleNavClick(item.id); }}>
                      {item.label}
                    </a>
                  </NavLink>
                ))}
              </NavLinks>
              
              <LanguageContainer ref={languageDropdownRef}>
                <AnimatePresence mode="wait">
                  {getLanguageButtons()}
                </AnimatePresence>
              </LanguageContainer>
              
              <MobileMenuButton 
                $isOpen={isMobileMenuOpen} 
                $isRTL={isRTL} 
                onClick={() => {
                  console.log('Mobile menu clicked, current state:', isMobileMenuOpen);
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }}
                aria-label={t.accessibility?.toggleMobileMenu || 'Toggle menu'}
              >
                <HamburgerLine $isOpen={isMobileMenuOpen} />
                <HamburgerLine $isOpen={isMobileMenuOpen} />
                <HamburgerLine $isOpen={isMobileMenuOpen} />
              </MobileMenuButton>
            </>
          )}
        </Nav>

        {/* Search and Filter Section for Products Page */}
        {isProductsPage && searchTerm !== undefined && (
          <SearchSection $isRTL={isRTL} $isMinimized={isSearchMinimized} onTouchStart={handleSearchTap}>
            <SearchInput
              $isRTL={isRTL}
              type="text"
              placeholder={t.searchProducts || 'Rechercher des produits...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm?.(e.target.value)}
            />
            
            <FiltersRow>
              <CategoryTabs>
                {categories.map((category) => (
                  <CategoryTab
                    key={category.id}
                    $isActive={selectedCategory === category.id}
                    $isRTL={isRTL}
                    onClick={() => setSelectedCategory?.(selectedCategory === category.id ? '' : category.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category.label}
                  </CategoryTab>
                ))}
              </CategoryTabs>
              
              <ViewToggle>
                <ViewButton 
                  $isActive={viewMode === 'grid'} 
                  onClick={() => setViewMode?.('grid')}
                >
                  ⚏
                </ViewButton>
                <ViewButton 
                  $isActive={viewMode === 'list'} 
                  onClick={() => setViewMode?.('list')}
                >
                  ☰
                </ViewButton>
              </ViewToggle>
              
              {hasActiveFilters && onClearFilters && (
                <ClearFiltersButton
                  $isRTL={isRTL}
                  onClick={onClearFilters}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✕ {t.clearFilters || 'Effacer'}
                </ClearFiltersButton>
              )}
            </FiltersRow>
          </SearchSection>
        )}
      </HeaderContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {console.log('Rendering mobile menu, isRTL:', isRTL)}
          <MobileMenuOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <MobileMenuContainer
              $isRTL={isRTL}
              initial={{ 
                x: isRTL ? 320 : -320, 
                opacity: 0 
              }}
              animate={{ 
                x: 0, 
                opacity: 1 
              }}
              exit={{ 
                x: isRTL ? 320 : -320, 
                opacity: 0 
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <MobileMenuContent>
                <MobileMenuHeader $isRTL={isRTL}>
                  <MobileMenuTitle $isRTL={isRTL}>{t.navigation || 'Navigation'}</MobileMenuTitle>
                </MobileMenuHeader>
                
                <MobileNavLinks>
                  {navItems.map((item) => (
                    <MobileNavLink
                      key={item.id}
                      $isActive={activeSection === item.id}
                      $isRTL={isRTL}
                      onClick={() => handleNavClick(item.id)}
                    >
                      {item.label}
                    </MobileNavLink>
                  ))}
                </MobileNavLinks>
              </MobileMenuContent>
              
              <MobileMenuFooter $isRTL={isRTL}>
                <MobileLanguageTitle $isRTL={isRTL}>
                  {t.language || 'Language'}
                </MobileLanguageTitle>
                <MobileLanguageButtons>
                  <MobileLanguageButton
                    $isActive={language === 'fr'}
                    $isRTL={isRTL}
                    onClick={() => handleLanguageChange('fr')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    FR
                  </MobileLanguageButton>
                  <MobileLanguageButton
                    $isActive={language === 'en'}
                    $isRTL={isRTL}
                    onClick={() => handleLanguageChange('en')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    EN
                  </MobileLanguageButton>
                  <MobileLanguageButton
                    $isActive={language === 'ar'}
                    $isRTL={isRTL}
                    onClick={() => handleLanguageChange('ar')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ع
                  </MobileLanguageButton>
                </MobileLanguageButtons>
              </MobileMenuFooter>
            </MobileMenuContainer>
          </MobileMenuOverlay>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
